import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { ArticleSchema } from '@/components/feature/SeoSchema';

export default function BasetaoShippingCost() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen">
      <Header />
      <ArticleSchema slug="basetao-shipping-cost" />
      <article className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-foreground-900 transition-colors whitespace-nowrap">Home</Link></li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li className="text-foreground-900 font-medium">Shipping Cost Guide</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase text-secondary-700 bg-secondary-100 rounded-full">Shipping</span>
              <span className="text-xs text-foreground-500">Updated August 2026</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-5">
              Basetao Shipping Cost Guide 2026 — Cheapest Methods Compared
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              International shipping costs can make or break your <strong>Basetao</strong> shopping experience. Understanding the different shipping methods, their costs, delivery speeds, and how to minimize expenses is essential for getting the best overall value. This guide breaks down everything you need to know about Basetao shipping in 2026.
            </p>
          </header>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Basetao Shipping Methods Overview</h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Basetao offers multiple international shipping carriers, each with distinct advantages depending on your destination country, package weight, urgency, and budget. The most commonly used methods include economy air mail, standard express services, and premium courier options like DHL, FedEx, and EMS. Choosing the right method requires balancing these factors against your specific needs.
              </p>
              <div className="overflow-x-auto rounded-xl border border-background-200/70 mt-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Shipping Method</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Avg Cost Range</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Delivery Speed</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground-900">Best For</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Economy Air Mail', '$8 — $25', '15 — 30 days', 'Budget-conscious, non-urgent orders'],
                      ['EUB / ePacket', '$10 — $30', '10 — 20 days', 'Small packages under 2kg'],
                      ['EMS', '$20 — $50', '7 — 15 days', 'Medium-value, moderate speed'],
                      ['DHL Express', '$30 — $70', '3 — 7 days', 'Urgent, high-value shipments'],
                      ['FedEx', '$28 — $65', '3 — 7 days', 'Reliable express delivery'],
                      ['Sea Freight', '$15 — $40', '25 — 45 days', 'Very large, heavy shipments'],
                    ].map((row, idx) => (
                      <tr key={idx} className="border-t border-background-200/50">
                        {row.map((cell, cIdx) => <td key={cIdx} className="px-4 py-3 text-foreground-600">{cell}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">How to Minimize Shipping Costs</h2>
              <ul className="space-y-3 ml-5">
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Consolidate packages:</strong> Combining multiple items into one shipment dramatically reduces per-item shipping cost.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Remove unnecessary packaging:</strong> Request removal of shoeboxes and excess packaging to reduce volumetric weight.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Choose economy for non-urgent orders:</strong> If you can wait, economy shipping saves significant money.</li>
                <li className="text-base text-foreground-600 leading-relaxed"><strong className="text-foreground-900">Compare carriers each time:</strong> Rates fluctuate; always check multiple options before selecting.</li>
              </ul>
            </section>

            <section className="p-6 rounded-xl bg-background-100 border border-background-200/70">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">FAQ</h2>
              <div className="space-y-5">
                {[
                  { q: 'What is the cheapest Basetao shipping method?', a: 'Economy Air Mail and EUB/ePacket are typically the most affordable options, with rates starting around $8-$10 for small packages. However, delivery can take up to 30 days.' },
                  { q: 'Does Basetao shipping include tracking?', a: 'Most shipping methods include tracking. Economy options may have limited tracking updates compared to express services like DHL or FedEx.' },
                  { q: 'Are there customs fees with Basetao shipping?', a: 'Customs duties depend on your destination country\'s regulations and the declared package value. Basetao allows you to declare customs values, but always check your local import rules.' },
                ].map((faq, idx) => (
                  <div key={idx}><h3 className="text-base font-semibold text-foreground-900 mb-1.5">{faq.q}</h3><p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p></div>
                ))}
              </div>
            </section>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors duration-300 whitespace-nowrap"><i className="ri-home-4-line"></i>Back to Homepage</Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-100 hover:bg-background-200 border border-background-300/60 rounded-full transition-colors duration-300 whitespace-nowrap">Visit Main Website<i className="ri-external-link-line"></i></a>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}